import React, { useState, useEffect, useContext, useCallback } from 'react';
import { AuthContext } from '../App';
import axios from 'axios';
import Navigation from '../components/Navigation';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { Trophy, Award, Flame, TrendingUp, Users, Target, Star, CheckCircle, Lock, Plus, Zap, Calendar, Gift, Crown, BookOpen } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import MyGroupsPanel from '../components/MyGroupsPanel';
import BibleReadingTracker from '../components/BibleReadingTracker';

const Gamification = () => {
  const { user, API } = useContext(AuthContext);
  const [stats, setStats] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [achievements, setAchievements] = useState([]);
  const [allAchievements, setAllAchievements] = useState({});
  const [loading, setLoading] = useState(true);
  const [gamificationEnabled, setGamificationEnabled] = useState(false);
  const [showInPublicLeaderboard, setShowInPublicLeaderboard] = useState(false);
  const [selectedGroup] = useState(null);
  const [groupLeaderboard] = useState([]);

  // New state for challenges and missions
  const [dailyChallenges, setDailyChallenges] = useState(null);
  const [weeklyMissions, setWeeklyMissions] = useState(null);
  const [levels, setLevels] = useState([]);

  const fetchGameData = useCallback(async () => {
    try {
      const [statsRes, leaderboardRes, achievementsRes, challengesRes, missionsRes, levelsRes, allAchievementsRes] = await Promise.all([
        axios.get(`${API}/gamification/stats`),
        axios.get(`${API}/gamification/leaderboard`),
        axios.get(`${API}/gamification/achievements`),
        axios.get(`${API}/gamification/daily-challenges`),
        axios.get(`${API}/gamification/weekly-missions`),
        axios.get(`${API}/gamification/levels`),
        axios.get(`${API}/gamification/all-achievements`)
      ]);

      setStats(statsRes.data);
      setLeaderboard(leaderboardRes.data);
      setAchievements(achievementsRes.data.achievements);
      setAllAchievements(allAchievementsRes.data);
      setGamificationEnabled(statsRes.data.gamification_enabled);
      setShowInPublicLeaderboard(statsRes.data.show_in_public_leaderboard);
      setDailyChallenges(challengesRes.data);
      setWeeklyMissions(missionsRes.data);
      setLevels(levelsRes.data);
    } catch (error) {
      toast.error('Failed to load gamification data');
    } finally {
      setLoading(false);
    }
  }, [API]);

  useEffect(() => {
    fetchGameData();
  }, [fetchGameData]);

  const handleCompleteChallenge = async (challengeId) => {
    try {
      const response = await axios.post(`${API}/gamification/daily-challenges/${challengeId}/complete`);
      if (response.data.success) {
        // Check if bonus was earned
        if (response.data.bonus_earned > 0) {
          // Show special celebration for bonus!
          toast.success(response.data.message, {
            duration: 5000,
            icon: '🎉'
          });
        } else {
          toast.success(response.data.message);
        }
        fetchGameData();
      } else {
        toast.info(response.data.message);
      }
    } catch (error) {
      toast.error('Failed to complete challenge');
    }
  };

  const handleToggleGamification = async (enabled) => {
    try {
      await axios.patch(`${API}/gamification/toggle`, { gamification_enabled: enabled });
      setGamificationEnabled(enabled);
      toast.success(enabled ? 'Gamification enabled!' : 'Gamification disabled');
    } catch (error) {
      toast.error('Failed to update settings');
    }
  };

  const handleTogglePublicLeaderboard = async (show) => {
    try {
      await axios.patch(`${API}/gamification/toggle`, { show_in_public_leaderboard: show });
      setShowInPublicLeaderboard(show);
      toast.success(show ? 'You are now visible on the public leaderboard' : 'You are now hidden from the public leaderboard');
      fetchGameData();
    } catch (error) {
      toast.error('Failed to update settings');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-teal-50">
        <Navigation />
        <div className="flex items-center justify-center h-[80vh]">
          <div className="text-lg text-slate-600">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-teal-50" data-testid="gamification-page">
      <Navigation />
      
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        <div className="mb-8 animate-fade-in">
          <h1 className="text-4xl font-heading font-bold text-[#2B6B7F] mb-2">
            Spiritual Journey Tracker
          </h1>
          <p className="text-slate-600">Encourage each other through friendly accountability</p>
        </div>

        {/* Enable/Disable Toggle */}
        <Card className="mb-8">
          <CardContent className="pt-6 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-[#2B6B7F]/10 flex items-center justify-center">
                  <Trophy className="w-6 h-6 text-[#2B6B7F]" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg text-slate-800">Gamification Features</h3>
                  <p className="text-sm text-slate-600">
                    Track streaks, earn achievements, and compare progress with others
                  </p>
                </div>
              </div>
              <Switch
                checked={gamificationEnabled}
                onCheckedChange={handleToggleGamification}
                data-testid="gamification-toggle"
              />
            </div>

            {gamificationEnabled && (
              <div className="pt-4 border-t border-slate-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-slate-800 mb-1">Public Leaderboard</h4>
                    <p className="text-sm text-slate-600">
                      Show my progress on the "All Members" leaderboard
                    </p>
                  </div>
                  <Switch
                    checked={showInPublicLeaderboard}
                    onCheckedChange={handleTogglePublicLeaderboard}
                    data-testid="public-leaderboard-toggle"
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {!gamificationEnabled ? (
          <Card>
            <CardContent className="pt-6 text-center py-12">
              <Target className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-700 mb-2">
                Enable Gamification to Get Started
              </h3>
              <p className="text-slate-600 mb-6 max-w-md mx-auto">
                Turn on gamification to track your streaks, earn achievements, and join the leaderboard.
              </p>
              <Button
                onClick={() => handleToggleGamification(true)}
                className="bg-[#2B6B7F] hover:bg-[#1E4D5F]"
              >
                Enable Gamification
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Level Progress Card */}
            {stats?.level && (
              <Card className="mb-8 bg-gradient-to-r from-[#2B6B7F] to-[#5FA8C1] text-white">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center text-3xl">
                        {stats.level.current.icon}
                      </div>
                      <div>
                        <p className="text-white/80 text-sm">Current Level</p>
                        <h2 className="text-2xl font-bold">{stats.level.current.name}</h2>
                        <p className="text-white/70 text-sm">Level {stats.level.current.level}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-3xl font-bold">{stats?.points || 0}</p>
                      <p className="text-white/80 text-sm">Total Points</p>
                    </div>
                  </div>
                  
                  {stats.level.next && (
                    <div>
                      <div className="flex justify-between text-sm mb-2">
                        <span>Progress to {stats.level.next.name}</span>
                        <span>{stats.level.points_to_next} points to go</span>
                      </div>
                      <Progress value={stats.level.progress} className="h-3 bg-white/20" />
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Stats Overview */}
            <div className="grid md:grid-cols-4 gap-6 mb-8">
              <Card className="card-hover" data-testid="stat-points">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-600 mb-1">Total Points</p>
                      <p className="text-3xl font-bold text-[#2B6B7F]">{stats?.points || 0}</p>
                    </div>
                    <div className="w-12 h-12 rounded-full bg-[#2B6B7F]/10 flex items-center justify-center">
                      <Star className="w-6 h-6 text-[#2B6B7F]" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="card-hover" data-testid="stat-journal-streak">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-600 mb-1">Journal Streak</p>
                      <p className="text-3xl font-bold text-[#D4A574]">{stats?.current_journal_streak || 0}</p>
                      <p className="text-xs text-slate-500">Best: {stats?.longest_journal_streak || 0}</p>
                    </div>
                    <div className="w-12 h-12 rounded-full bg-[#D4A574]/10 flex items-center justify-center">
                      <Flame className="w-6 h-6 text-[#D4A574]" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="card-hover" data-testid="stat-checkin-streak">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-600 mb-1">Check-in Streak</p>
                      <p className="text-3xl font-bold text-[#5FA8C1]">{stats?.current_checkin_streak || 0}</p>
                      <p className="text-xs text-slate-500">Best: {stats?.longest_checkin_streak || 0}</p>
                    </div>
                    <div className="w-12 h-12 rounded-full bg-[#5FA8C1]/10 flex items-center justify-center">
                      <Flame className="w-6 h-6 text-[#5FA8C1]" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="card-hover" data-testid="stat-achievements">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-600 mb-1">Achievements</p>
                      <p className="text-3xl font-bold text-[#7BA888]">{stats?.achievements_count || 0}</p>
                      <p className="text-xs text-slate-500">of {allAchievements?.total_count || achievements.length}</p>
                    </div>
                    <div className="w-12 h-12 rounded-full bg-[#7BA888]/10 flex items-center justify-center">
                      <Award className="w-6 h-6 text-[#7BA888]" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Daily Challenges & Weekly Missions */}
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              {/* Daily Challenges */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-[#2B6B7F]">
                    <Zap className="w-5 h-5" />
                    Daily Challenges
                  </CardTitle>
                  <CardDescription>
                    {dailyChallenges?.completed_count || 0} of {dailyChallenges?.total_count || 0} completed today
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {/* Daily Streak Bonus Progress */}
                  <div className={`p-4 rounded-lg border-2 ${
                    dailyChallenges?.bonus_earned 
                      ? 'bg-gradient-to-r from-yellow-50 to-amber-50 border-yellow-400' 
                      : 'bg-gradient-to-r from-purple-50 to-indigo-50 border-purple-200'
                  }`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Gift className={`w-5 h-5 ${dailyChallenges?.bonus_earned ? 'text-yellow-600' : 'text-purple-600'}`} />
                        <span className={`font-semibold ${dailyChallenges?.bonus_earned ? 'text-yellow-700' : 'text-purple-700'}`}>
                          Daily Streak Bonus
                        </span>
                      </div>
                      <Badge className={dailyChallenges?.bonus_earned ? 'bg-yellow-500' : 'bg-purple-500'}>
                        +{dailyChallenges?.bonus_available || 65} pts
                      </Badge>
                    </div>
                    {dailyChallenges?.bonus_earned ? (
                      <div className="flex items-center gap-2 text-yellow-700">
                        <CheckCircle className="w-4 h-4" />
                        <span className="text-sm font-medium">Bonus Earned Today! 🎉</span>
                      </div>
                    ) : (
                      <>
                        <p className="text-xs text-purple-600 mb-2">
                          Complete all 5 challenges for 2x points!
                        </p>
                        <div className="flex gap-1">
                          {[0, 1, 2, 3, 4].map((slot) => (
                            <div 
                              key={`challenge-slot-${slot}`}
                              className={`flex-1 h-2 rounded-full ${
                                slot < (dailyChallenges?.completed_count || 0)
                                  ? 'bg-purple-500'
                                  : 'bg-purple-200'
                              }`}
                            />
                          ))}
                        </div>
                      </>
                    )}
                  </div>

                  {dailyChallenges?.challenges?.map((challenge) => (
                    <div 
                      key={challenge.id}
                      className={`flex items-center justify-between p-3 rounded-lg border ${
                        challenge.completed 
                          ? 'bg-green-50 border-green-200' 
                          : 'bg-slate-50 border-slate-200'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        {challenge.completed ? (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        ) : (
                          <div className="w-5 h-5 rounded-full border-2 border-slate-300" />
                        )}
                        <div>
                          <p className={`font-medium ${challenge.completed ? 'text-green-700' : 'text-slate-700'}`}>
                            {challenge.name}
                          </p>
                          <p className="text-xs text-slate-500">{challenge.description}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant={challenge.completed ? 'default' : 'secondary'} className={challenge.completed ? 'bg-green-600' : ''}>
                          +{challenge.points}
                        </Badge>
                        {!challenge.completed && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleCompleteChallenge(challenge.id)}
                            className="text-xs"
                          >
                            Complete
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Weekly Missions */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-[#2B6B7F]">
                    <Calendar className="w-5 h-5" />
                    Weekly Missions
                  </CardTitle>
                  <CardDescription>
                    {weeklyMissions?.completed_count || 0} of {weeklyMissions?.total_count || 0} completed this week
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {weeklyMissions?.missions?.map((mission) => (
                    <div key={mission.id} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {mission.completed ? (
                            <CheckCircle className="w-4 h-4 text-green-600" />
                          ) : (
                            <Target className="w-4 h-4 text-slate-400" />
                          )}
                          <span className={`font-medium ${mission.completed ? 'text-green-700' : 'text-slate-700'}`}>
                            {mission.name}
                          </span>
                        </div>
                        <Badge variant={mission.completed ? 'default' : 'secondary'} className={mission.completed ? 'bg-green-600' : ''}>
                          +{mission.points}
                        </Badge>
                      </div>
                      <p className="text-xs text-slate-500 ml-6">{mission.description}</p>
                      <div className="ml-6">
                        <div className="flex justify-between text-xs text-slate-500 mb-1">
                          <span>Progress: {mission.progress}/{mission.requirement}</span>
                          <span>{mission.percentage}%</span>
                        </div>
                        <Progress value={mission.percentage} className="h-2" />
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>

            {/* Tabs for Achievements, Leaderboard, Groups, Bible Tour */}
            <Tabs defaultValue="achievements" className="space-y-6">
              <TabsList className="grid grid-cols-2 sm:grid-cols-4 h-auto bg-slate-100 p-1 rounded-lg gap-1">
                <TabsTrigger value="achievements" className="flex items-center gap-2 py-3">
                  <Award className="w-4 h-4" />
                  Achievements
                </TabsTrigger>
                <TabsTrigger value="leaderboard" className="flex items-center gap-2 py-3">
                  <TrendingUp className="w-4 h-4" />
                  Leaderboard
                </TabsTrigger>
                <TabsTrigger value="groups" className="flex items-center gap-2 py-3">
                  <Users className="w-4 h-4" />
                  Groups
                </TabsTrigger>
                <TabsTrigger value="bible-tour" className="flex items-center gap-2 py-3" data-testid="tab-bible-tour">
                  <BookOpen className="w-4 h-4" />
                  Bible Tour
                </TabsTrigger>
              </TabsList>

              <TabsContent value="achievements">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-[#2B6B7F]">Your Achievements</CardTitle>
                    <CardDescription>
                      Unlock achievements by completing spiritual activities
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {achievements.map((achievement) => {
                        const isUnlocked = stats?.achievements?.includes(achievement.id);
                        const achievementData = allAchievements?.achievements?.[achievement.id] || achievement;
                        
                        return (
                          <div
                            key={achievement.id}
                            className={`p-4 rounded-xl border-2 transition-all ${
                              isUnlocked
                                ? 'border-[#7BA888] bg-[#7BA888]/5'
                                : 'border-slate-200 bg-slate-50 opacity-60'
                            }`}
                          >
                            <div className="flex items-start gap-3">
                              <div className={`w-12 h-12 rounded-full flex items-center justify-center text-2xl ${
                                isUnlocked ? 'bg-[#7BA888]/20' : 'bg-slate-200'
                              }`}>
                                {isUnlocked ? (achievementData.icon || '🏆') : <Lock className="w-5 h-5 text-slate-400" />}
                              </div>
                              <div className="flex-1">
                                <h4 className={`font-semibold ${isUnlocked ? 'text-[#2B6B7F]' : 'text-slate-500'}`}>
                                  {achievement.name}
                                </h4>
                                <p className="text-xs text-slate-500 mt-1">
                                  {achievement.description}
                                </p>
                                <Badge variant="secondary" className="mt-2">
                                  +{achievement.points} pts
                                </Badge>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="leaderboard">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-[#2B6B7F]">Church Leaderboard</CardTitle>
                    <CardDescription>
                      See how you compare with other members (only shows those who opted in)
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {leaderboard.length === 0 ? (
                      <div className="text-center py-8 text-slate-500">
                        <Users className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                        <p>No members on the public leaderboard yet.</p>
                        <p className="text-sm">Be the first to enable public visibility!</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {leaderboard.map((member, index) => (
                          <div
                            key={member.user_id ?? `lb-${index}`}
                            className={`flex items-center gap-4 p-4 rounded-xl ${
                              member.user_id === user?.id
                                ? 'bg-[#2B6B7F]/10 border-2 border-[#2B6B7F]'
                                : 'bg-slate-50'
                            }`}
                          >
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                              index === 0 ? 'bg-yellow-400 text-yellow-900' :
                              index === 1 ? 'bg-slate-300 text-slate-700' :
                              index === 2 ? 'bg-amber-600 text-white' :
                              'bg-slate-200 text-slate-600'
                            }`}>
                              {index + 1}
                            </div>
                            <div className="flex-1">
                              <p className="font-semibold text-slate-800">
                                {member.name}
                                {member.user_id === user?.id && (
                                  <span className="text-[#2B6B7F] ml-2">(You)</span>
                                )}
                              </p>
                              <div className="flex gap-4 text-sm text-slate-500">
                                <span>🔥 {member.journal_streak} day streak</span>
                                <span>🏆 {member.achievements_count} achievements</span>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-2xl font-bold text-[#2B6B7F]">{member.points}</p>
                              <p className="text-xs text-slate-500">points</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="groups">
                <div className="space-y-6">
                  <MyGroupsPanel
                    title="My Groups"
                    description="Private, invite-only accountability groups. Share your group's invite code to add people."
                  />

                  {selectedGroup && (
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-[#2B6B7F]">{selectedGroup.name}</CardTitle>
                        <CardDescription>Group leaderboard</CardDescription>
                      </CardHeader>
                      <CardContent>
                        {groupLeaderboard.length === 0 ? (
                          <p className="text-slate-500 text-center py-4">
                            No activity in this group yet
                          </p>
                        ) : (
                          <div className="space-y-2">
                            {groupLeaderboard.map((member, index) => (
                              <div
                                key={member.user_id ?? `glb-${index}`}
                                className="flex items-center gap-3 p-2 rounded-lg bg-slate-50"
                              >
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                                  index === 0 ? 'bg-yellow-400' : 'bg-slate-200'
                                }`}>
                                  {index + 1}
                                </div>
                                <div className="flex-1">
                                  <p className="font-medium text-sm">{member.name}</p>
                                </div>
                                <p className="font-bold text-[#2B6B7F]">{member.points}</p>
                              </div>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  )}
                </div>
              </TabsContent>

              {/* BIBLE TOUR TAB */}
              <TabsContent value="bible-tour">
                <BibleReadingTracker />
              </TabsContent>
            </Tabs>
          </>
        )}
      </div>
    </div>
  );
};

export default Gamification;
